import { Mail, MessageSquare } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useSignup, useVerifyPhoneOTP } from "@/api/hooks";
import { PhoneInput } from "@/components/PhoneInput";
import { TERMS_VERSION } from "@/routes/legal/TermsPage";

/** Storage key for "user wants to subscribe to this tier after
 *  finishing signup". Written here, read by MagicLinkConsumePage. */
const PENDING_PLAN_STORAGE_KEY = "pencilout_pending_plan";

const VALID_PLAN_TIERS = new Set(["free", "pro", "crew"]);

type Method = "email" | "phone";

/** Self-serve contractor signup.
 *
 * Two paths:
 * - **Email**: server creates a trial Org + first ``org_admin``
 *   User and emails a magic link to finish signing in.
 * - **Phone**: server creates the same Org + User but with the
 *   user's phone instead of email; we surface a 6-digit code entry
 *   page that hits ``/auth/phone/verify`` to claim the session.
 *
 * If the URL has ``?plan=pro`` or ``?plan=crew``, we stash that in
 * localStorage. After magic-link consume / phone verify, the route
 * picks it up and redirects to ``/billing?plan=<tier>`` so the user
 * lands directly on the upgrade flow they came from. */
export function SignupPage() {
  const [method, setMethod] = useState<Method>("email");
  const [companyName, setCompanyName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [searchParams] = useSearchParams();
  const signup = useSignup();
  const verifyPhone = useVerifyPhoneOTP();
  const navigate = useNavigate();

  // After signup succeeds: email path → "check inbox", phone path →
  // surface the code-entry form.
  const emailSubmitted = signup.isSuccess && method === "email";
  const phoneCodeRequested = signup.isSuccess && method === "phone";

  // Persist the requested plan tier so the post-consume redirect
  // can pick it up. Cleaned up by MagicLinkConsumePage / verify
  // success handler after read.
  useEffect(() => {
    const requestedPlan = searchParams.get("plan");
    if (requestedPlan && VALID_PLAN_TIERS.has(requestedPlan)) {
      try {
        window.localStorage.setItem(PENDING_PLAN_STORAGE_KEY, requestedPlan);
      } catch {
        /* localStorage unavailable (privacy mode); silently degrade */
      }
    }
  }, [searchParams]);

  // Auto-submit phone code at 6 digits (mirrors iOS PhoneOTPSheet).
  // biome-ignore lint/correctness/useExhaustiveDependencies: verifyPhone.mutateAsync identity changes per render; .isPending flips inside the call. Including either re-fires the verify mid-flight and double-submits the OTP.
  useEffect(() => {
    if (phoneCodeRequested && code.length === 6 && /^\d{6}$/.test(code) && !verifyPhone.isPending) {
      verifyPhone
        .mutateAsync({ phone_number: phoneNumber, code })
        .then(() => {
          // Pull pending plan if any.
          let dest = "/";
          try {
            const stashed = window.localStorage.getItem(PENDING_PLAN_STORAGE_KEY);
            if (stashed && VALID_PLAN_TIERS.has(stashed)) {
              window.localStorage.removeItem(PENDING_PLAN_STORAGE_KEY);
              dest = `/billing?plan=${stashed}`;
            }
          } catch {
            /* ignore */
          }
          navigate(dest);
        })
        .catch(() => {
          setCode("");
        });
    }
  }, [code, phoneCodeRequested, phoneNumber, navigate]);

  const incomingPlan = searchParams.get("plan");
  const planLabel =
    incomingPlan && VALID_PLAN_TIERS.has(incomingPlan)
      ? incomingPlan.charAt(0).toUpperCase() + incomingPlan.slice(1)
      : null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!displayName.trim() || !companyName.trim() || !agreed) return;
    if (method === "email" && !email.trim()) return;
    if (method === "phone" && phoneNumber.replace(/\D/g, "").length < 10) return;
    try {
      await signup.mutateAsync({
        company_name: companyName.trim(),
        display_name: displayName.trim(),
        email: method === "email" ? email.trim() : undefined,
        phone_number: method === "phone" ? phoneNumber.trim() : undefined,
        terms_accepted_version: TERMS_VERSION,
      });
    } catch {
      /* error surfaces below via signup.error */
    }
  }

  async function onPhoneVerifyManual(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (code.length !== 6) return;
    try {
      await verifyPhone.mutateAsync({
        phone_number: phoneNumber.trim(),
        code,
      });
      let dest = "/";
      try {
        const stashed = window.localStorage.getItem(PENDING_PLAN_STORAGE_KEY);
        if (stashed && VALID_PLAN_TIERS.has(stashed)) {
          window.localStorage.removeItem(PENDING_PLAN_STORAGE_KEY);
          dest = `/billing?plan=${stashed}`;
        }
      } catch {
        /* ignore */
      }
      navigate(dest);
    } catch {
      setCode("");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <span className="brand-wordmark text-3xl">PencilOut</span>
          <p className="mt-3 text-sm text-graphite-soft">
            Video walkthroughs in. Signed quotes out. Free trial — no card up front.
          </p>
          {planLabel && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-pencil-yellow-soft px-3 py-1 text-xs font-mono uppercase tracking-wider text-graphite">
              You'll be set up on <strong>{planLabel}</strong> after signup
            </p>
          )}
        </div>

        {emailSubmitted ? (
          <div className="rounded-lg border border-rule bg-paper-warm p-6 text-center">
            <Mail className="mx-auto h-6 w-6 text-pencil-yellow" aria-hidden />
            <h1 className="mt-3 font-mono text-lg font-bold tracking-tight">Check your inbox</h1>
            <p className="mt-2 text-sm text-graphite-soft">
              {signup.data?.message ??
                `We sent a sign-in link to ${email.trim()}. The link is good for 15 minutes.`}
            </p>
            <p className="mt-4 text-xs text-graphite-soft">
              Trouble? Try the{" "}
              <Link to="/login" className="text-graphite underline">
                sign-in
              </Link>{" "}
              flow with the same email — it'll send another link.
            </p>
          </div>
        ) : phoneCodeRequested ? (
          <form
            onSubmit={onPhoneVerifyManual}
            className="space-y-4 rounded-lg border border-rule bg-paper-warm p-6"
          >
            <MessageSquare className="mx-auto h-6 w-6 text-pencil-yellow" aria-hidden />
            <h1 className="text-center font-mono text-lg font-bold tracking-tight">
              Enter your code
            </h1>
            <p className="text-center text-sm text-graphite-soft">
              We just texted a 6-digit code to{" "}
              <span className="font-mono text-graphite">{phoneNumber.trim()}</span>.
            </p>
            <label className="block">
              <span className="label-mono">Code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-3 text-center font-mono text-xl tracking-[0.5em] text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>

            {verifyPhone.error && (
              <p className="text-sm text-red-pencil">
                {verifyPhone.error instanceof ApiError
                  ? verifyPhone.error.detail
                  : "Couldn't verify the code."}
              </p>
            )}

            <button
              type="submit"
              disabled={verifyPhone.isPending || code.length !== 6}
              className="w-full rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              {verifyPhone.isPending ? "Verifying…" : "Verify & sign in"}
            </button>

            <div className="flex justify-between text-xs">
              <button
                type="button"
                onClick={() => {
                  signup.reset();
                  setCode("");
                }}
                className="font-mono text-graphite-soft underline hover:text-graphite"
              >
                Use a different number
              </button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={onSubmit}
            className="space-y-4 rounded-lg border border-rule bg-paper-warm p-6"
          >
            <div className="flex gap-1 rounded-md border border-rule bg-paper p-1">
              <button
                type="button"
                onClick={() => setMethod("email")}
                className={[
                  "flex-1 rounded px-3 py-1.5 font-mono text-xs transition",
                  method === "email"
                    ? "bg-graphite text-paper"
                    : "text-graphite-soft hover:text-graphite",
                ].join(" ")}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => setMethod("phone")}
                className={[
                  "flex-1 rounded px-3 py-1.5 font-mono text-xs transition",
                  method === "phone"
                    ? "bg-graphite text-paper"
                    : "text-graphite-soft hover:text-graphite",
                ].join(" ")}
              >
                Text
              </button>
            </div>

            <label className="block">
              <span className="label-mono">Company name</span>
              <input
                type="text"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Smith Remodeling LLC"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>

            <label className="block">
              <span className="label-mono">Your name</span>
              <input
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="John Smith"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>

            {method === "email" ? (
              <label className="block">
                <span className="label-mono">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@smithremodeling.com"
                  className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
                />
                <span className="mt-1 block text-xs text-graphite-soft">
                  We'll email you a one-time sign-in link. No password.
                </span>
              </label>
            ) : (
              // biome-ignore lint/a11y/noLabelWithoutControl: <PhoneInput> renders the input internally as a descendant of this <label>; the DOM association works at runtime even though biome can't see through the component boundary.
              <label className="block">
                <span className="label-mono">Phone</span>
                <PhoneInput
                  required
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="(704) 555-1234"
                  className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
                />
                <span className="mt-1 block text-xs text-graphite-soft">
                  We'll text you a 6-digit code. No password.
                </span>
              </label>
            )}

            <label className="flex items-start gap-2 text-xs text-graphite-soft">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                I agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-graphite underline hover:no-underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-graphite underline hover:no-underline"
                >
                  Privacy Policy
                </Link>
                . The Free plan is 3 quotes/month with no card up front. Upgrade to Pro or Crew
                anytime for higher limits.
              </span>
            </label>

            {signup.error && (
              <p className="text-sm text-red-pencil">
                {signup.error instanceof ApiError
                  ? signup.error.detail
                  : "Could not create your account."}
              </p>
            )}

            <button
              type="submit"
              disabled={
                signup.isPending ||
                !displayName.trim() ||
                !companyName.trim() ||
                !agreed ||
                (method === "email" && !email.trim()) ||
                (method === "phone" && phoneNumber.replace(/\D/g, "").length < 10)
              }
              className="w-full rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              {signup.isPending
                ? "Creating account…"
                : method === "phone"
                  ? "Create account & text me a code"
                  : "Create account"}
            </button>
          </form>
        )}

        <p className="mt-6 text-center font-mono text-xs text-graphite-soft">
          Already have an account?{" "}
          <Link to="/login" className="text-graphite underline hover:no-underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
